import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import LeadForm from "@/components/LeadForm";
import { FadeIn, MaskedLines, Chapter } from "@/components/Motion";
import { IMAGES } from "@/data/content";

const CUSTOMIZATION = [
  { title: "Seat", text: "Upholstery, colours, armrests, belts and ergonomics to your specification." },
  { title: "Rail", text: "Rail profiles and finishes matched to your brand's design language." },
  { title: "Colour", text: "Full RAL programme across seat, rail, carriage and covers." },
  { title: "Brand", text: "Your badge, your name, your product — moulded, printed, engraved." },
  { title: "Packaging", text: "Retail-ready branded packaging and unboxing experience." },
  { title: "Software", text: "Custom display graphics, behaviour parameters and diagnostics." },
  { title: "Electronics", text: "Control architecture adapted to your market and certification needs." },
  { title: "Accessories", text: "Covers, key switches, call stations and powered options." },
  { title: "Documentation", text: "Manuals, install guides and technical files in your identity." },
  { title: "Certification Support", text: "EN 81-40 and CE pathway managed with our engineering team." },
];

const PROCESS = [
  { step: "Idea", text: "Your market requirement, our feasibility and cost engineering." },
  { step: "Concept", text: "Product definition, branding scope and industrial design direction." },
  { step: "CAD", text: "Complete 3D engineering of your customised configuration." },
  { step: "Prototype", text: "Functional prototypes for evaluation, testing and sign-off." },
  { step: "Certification", text: "Type testing, documentation and CE / EN 81-40 conformity." },
  { step: "Production", text: "Series manufacturing with quality gates at every station." },
  { step: "Delivery", text: "Consolidated logistics to your warehouse, anywhere in Europe." },
];

const COMMERCIAL = [
  { title: "MOQ that scales", text: "Pilot runs from 50 units. Volume programmes with dedicated production slots and framework pricing." },
  { title: "Quality control", text: "100% end-of-line functional test, batch traceability and dealer-visible QC reporting." },
  { title: "Industrialization", text: "Our factory becomes your factory — rail bending, coating, electronics and assembly under one European roof." },
];

export default function OEM() {
  return (
    <>
      <PageHero
        overline="OEM Manufacturing"
        lines={["Your Stairlift.", "Your Design.", "Your Brand."]}
        sub="Sell a premium European stairlift under your own name. We engineer, certify and manufacture — you own the customer, the margin and the market."
        image={IMAGES.factory}
      />

      <section className="bg-white py-24" data-testid="oem-customization">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="01" label="Complete Customization" />
          <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950">
            <MaskedLines lines={["Every surface,", "every detail — yours"]} />
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {CUSTOMIZATION.map((c, i) => (
              <FadeIn key={c.title} delay={(i % 5) * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200/80 bg-[#F8F9FA] p-6 hover:border-signal/40 transition-colors duration-300" data-testid={`oem-custom-${i}`}>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-display text-base font-semibold tracking-tight text-slate-950">{c.title}</h3>
                  <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{c.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-obsidian noise-dark py-24 sm:py-32" data-testid="oem-process">
        <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="02" label="Manufacturing Process" light />
          <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05]">
            <MaskedLines lines={["From idea to delivery,", "seven controlled steps"]} />
          </h2>
          <div className="mt-14">
            {PROCESS.map((p, i) => (
              <FadeIn key={p.step} delay={Math.min(i * 0.05, 0.3)}>
                <div className="group flex items-baseline gap-6 sm:gap-10 py-6 border-b border-white/10 first:border-t" data-testid={`oem-step-${i}`}>
                  <span className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-outline-light w-16 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white sm:w-48 shrink-0 group-hover:text-signal transition-colors">
                    {p.step}
                  </h3>
                  <p className="hidden sm:block text-sm text-slate-400 leading-relaxed">{p.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24" data-testid="oem-commercial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="03" label="Production & Quality" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {COMMERCIAL.map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200/80 bg-[#F8F9FA] p-7" data-testid={`oem-commercial-${i}`}>
                  <h3 className="font-display text-lg font-semibold tracking-tight text-slate-950">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] border-y border-slate-200/70 py-24" data-testid="oem-lead">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Chapter index="04" label="Start a Project" />
            <h2 className="font-display text-2xl sm:text-4xl font-medium tracking-tight text-slate-950 leading-[1.05]">
              <MaskedLines lines={["Develop your", "own stairlift"]} />
            </h2>
            <FadeIn delay={0.15}>
              <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-md">
                Tell us about your market, volumes and brand ambitions. Our OEM team responds with feasibility, timelines and pilot pricing within 48 hours.
              </p>
            </FadeIn>
          </div>
          <FadeIn delay={0.2}>
            <div className="rounded-2xl border border-slate-200/80 bg-white p-7 sm:p-9">
              <LeadForm defaultType="oem" context="OEM page enquiry" />
            </div>
          </FadeIn>
        </div>
      </section>

      <CTABand
        overline="OEM"
        lines={["Your brand deserves", "a European factory."]}
        primary={{ to: "/contact?type=oem", label: "Develop Your Own Stairlift", testid: "oem-cta-develop" }}
      />
    </>
  );
}
